// utils.js - Common utility functions

import store from "./store.js";

/**
 * Encrypts data using a simple XOR cipher
 * @param {Object} data - The data to encrypt
 * @param {string} key - The encryption key
 * @returns {string|null} The encrypted data as a base64 string or null if encryption fails
 */
function encryptData(data, key) {
  try {
    const stringData = JSON.stringify(data);
    const encryptedArray = [];
    for (let i = 0; i < stringData.length; i++) {
      const keyChar = key.charCodeAt(i % key.length);
      const encryptedChar = stringData.charCodeAt(i) ^ keyChar;
      encryptedArray.push(encryptedChar);
    }
    return btoa(encryptedArray.join(","));
  } catch (error) {
    console.error("Encryption error:", error);
    return null;
  }
}

/**
 * Decrypts data that was encrypted with encryptData
 * @param {string} encryptedData - The encrypted data as a base64 string
 * @param {string} key - The encryption key
 * @returns {Object|null} The decrypted data or null if decryption fails
 */
function decryptData(encryptedData, key) {
  try {
    const encryptedArray = atob(encryptedData).split(",").map(Number);
    let decryptedString = "";

    for (let i = 0; i < encryptedArray.length; i++) {
      const keyChar = key.charCodeAt(i % key.length);
      const decryptedChar = encryptedArray[i] ^ keyChar;
      decryptedString += String.fromCharCode(decryptedChar);
    }

    return JSON.parse(decryptedString);
  } catch (error) {
    console.error("Decryption error:", error);
    return null;
  }
}

/**
 * Formats message content with paragraphs and line breaks
 * @param {string} text - The text to format
 * @returns {string} Formatted HTML
 */
function formatMessageContent(text) {
  if (!text) return "";

  // Sanitize text to prevent basic HTML injection
  const sanitizedText = text.replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Split into paragraphs based on double newlines, process each paragraph
  const paragraphs = sanitizedText
    .split('\n\n') // Split by double newline for paragraphs
    .filter(block => block.trim() !== '') // Remove empty blocks
    .map(block => {
      // Replace single newlines within the block with <br>
      // Ensure we target the actual newline character \n
      const linesWithBreaks = block.replace(/\n/g, '<br>');
      // Wrap the processed block in <p> tags
      return `<p>${linesWithBreaks}</p>`;
    })
    .join(''); // Join the paragraphs back together

  // Apply bold formatting to the paragraph-wrapped content
  return paragraphs
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<strong>$1</strong>"); // Keep both * and ** for bold
}

/**
 * Gets a unique device ID, generating one if needed
 * @returns {string} The device ID
 */
function getDeviceId() {
    let deviceId = localStorage.getItem("quantum_device_id");

    if (!deviceId) {
        deviceId = sessionStorage.getItem("quantum_device_id");
    }

    if (!deviceId) {
        const cookieMatch = document.cookie.match(/quantum_device_id=([^;]+)/);
        if (cookieMatch) deviceId = cookieMatch[1];
    }

    if (!deviceId) {
        const idbPromise = new Promise((resolve) => {
            if (!window.indexedDB) {
                resolve(null);
                return;
            }

            const request = indexedDB.open("QuantumDB", 1);

            request.onupgradeneeded = function (event) {
                const db = event.target.result;
                if (!db.objectStoreNames.contains("deviceInfo")) {
                    db.createObjectStore("deviceInfo", { keyPath: "id" });
                }
            };

            request.onsuccess = function (event) {
                const db = event.target.result;
                const transaction = db.transaction(["deviceInfo"], "readonly");
                const store = transaction.objectStore("deviceInfo");
                const getRequest = store.get("deviceId");

                getRequest.onsuccess = function () {
                    if (getRequest.result) {
                        resolve(getRequest.result.value);
                    } else {
                        resolve(null);
                    }
                };

                getRequest.onerror = function () {
                    resolve(null);
                };
            };

            request.onerror = function () {
                resolve(null);
            };
        });

        idbPromise.then((idbDeviceId) => {
            if (idbDeviceId && !deviceId) {
                deviceId = idbDeviceId;
                try {
                    localStorage.setItem("quantum_device_id", deviceId);
                    sessionStorage.setItem("quantum_device_id", deviceId);

                    const expiryDate = new Date();
                    expiryDate.setFullYear(expiryDate.getFullYear() + 10);
                    document.cookie = `quantum_device_id=${deviceId}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Strict`;
                } catch (e) {
                    console.error("Error storing retrieved device ID:", e);
                }
            }
        });
    }

    if (!deviceId) {
        let nonce = "";
        const characters =
            "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        const length = 32;

        if (window.crypto && window.crypto.getRandomValues) {
            const values = new Uint8Array(length);
            window.crypto.getRandomValues(values);
            for (let i = 0; i < length; i++) {
                nonce += characters.charAt(values[i] % characters.length);
            }
        } else {
            for (let i = 0; i < length; i++) {
                nonce += characters.charAt(
                    Math.floor(Math.random() * characters.length)
                );
            }
        }

        const timestamp = Date.now().toString();
        deviceId = nonce + "-" + timestamp;

        storeDeviceId(deviceId);
    }

    return deviceId;
}

/**
 * Stores a device ID in multiple storage locations for persistence
 * @param {string} deviceId - The device ID to store
 */
function storeDeviceId(deviceId) {
    try {
        localStorage.setItem("quantum_device_id", deviceId);
        sessionStorage.setItem("quantum_device_id", deviceId);

        const expiryDate = new Date();
        expiryDate.setFullYear(expiryDate.getFullYear() + 10);
        document.cookie = `quantum_device_id=${deviceId}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Strict`;

        storeInIndexedDB(deviceId);

        if (navigator.storage && navigator.storage.persistent) {
            navigator.storage.persist().then((isPersisted) => {});
        }
    } catch (e) {
        console.error("Error storing device ID:", e);
    }
}

/**
 * Stores a device ID in IndexedDB
 * @param {string} deviceId - The device ID to store
 */
function storeInIndexedDB(deviceId) {
    if (!window.indexedDB) {
        return;
    }

    try {
        const request = indexedDB.open("QuantumDB", 1);

        request.onupgradeneeded = function (event) {
            const db = event.target.result;
            if (!db.objectStoreNames.contains("deviceInfo")) {
                db.createObjectStore("deviceInfo", { keyPath: "id" });
            }
        };

        request.onsuccess = function (event) {
            const db = event.target.result;
            const transaction = db.transaction(["deviceInfo"], "readwrite");
            const store = transaction.objectStore("deviceInfo");

            store.put({ id: "deviceId", value: deviceId });
        };
    } catch (e) {
        console.error("Error storing in IndexedDB:", e);
    }
}

/**
 * Shows a confirmation dialog
 * @param {string} title - The dialog title
 * @param {string} message - The dialog message
 * @param {Function} callback - Function to call with the result (true or false)
 */
function showConfirm(title, message, callback) {
  const confirmTitle = document.getElementById("confirmTitle");
  const confirmMessage = document.getElementById("confirmMessage");
  const confirmModal = document.getElementById("confirmModal");

  confirmTitle.textContent = title;
  confirmMessage.textContent = message;

  // Store the callback in the central store
  store.setConfirmCallback(callback);

  confirmModal.style.display = "flex";
}

/**
 * Gets the color for a notification type
 * @param {string} type - The notification type
 * @returns {string} The color value
 */
function getNotificationColor(type) {
  switch (type) {
    case "success":
      return "var(--success, #4CAF50)";
    case "error":
      return "var(--error, #F44336)";
    case "warning":
      return "var(--warning, #FF9800)";
    default:
      return "var(--accent, #2196F3)";
  }
}

/**
 * Creates and shows a notification
 * @param {string} message - The notification message
 * @param {string} type - The type of notification (info, success, error, warning)
 * @returns {HTMLElement} The notification element
 */
function showNotification(message, type = "info") {
  const notification = document.createElement("div");
  notification.style.position = "fixed";
  notification.style.bottom = "20px";
  notification.style.right = "20px";
  notification.style.padding = "10px 20px";
  notification.style.backgroundColor = getNotificationColor(type);
  notification.style.color = "white";
  notification.style.borderRadius = "4px";
  notification.style.boxShadow = "0 2px 10px rgba(0, 0, 0, 0.2)";
  notification.style.zIndex = "1000";
  notification.style.animation = "fade-in 0.3s ease-out";
  notification.textContent = message;

  document.body.appendChild(notification);

  setTimeout(() => {
    notification.style.opacity = "0";
    notification.style.transition = "opacity 0.5s ease-out";
    setTimeout(() => {
      if (notification.parentNode) {
        document.body.removeChild(notification);
      }
    }, 500);
  }, 3000);

  return notification;
}

/**
 * Updates an existing notification
 * @param {HTMLElement} notification - The notification element to update
 * @param {string} message - The new message
 * @param {string} type - The new notification type
 */
function updateNotification(notification, message, type = "info") {
  if (notification && notification.parentNode) {
    notification.textContent = message;
    notification.style.backgroundColor = getNotificationColor(type);
  }
}

// Ajouter cette fonction dans utils.js

/**
 * Migre les données des anciennes clés alaric vers les nouvelles clés quantum
 * et supprime les anciennes clés après la migration
 */
function migrateStorageData() {
  // Tableau de paires [ancienneClé, nouvelleClé]
  const keyPairs = [
    ["alaricDisclaimerAccepted", "quantumDisclaimerAccepted"],
    ["alaricAuthToken", "quantumAuthToken"],
    ["alaricGuestCount", "quantumGuestCount"],
    ["alaricGuestUsage", "quantumGuestUsage"],
    ["alaricApiSettings", "quantumApiSettings"],
    ["alaricGuestConversations", "quantumGuestConversations"],
  ];

  // Migrer chaque paire de clés et supprimer les anciennes
  keyPairs.forEach(([oldKey, newKey]) => {
    const oldValue = localStorage.getItem(oldKey);
    if (oldValue !== null) {
      localStorage.setItem(newKey, oldValue);
      localStorage.removeItem(oldKey);
      console.log(`Migré et supprimé ${oldKey} vers ${newKey}`);
    }
  });

  // Migrer les conversations des wallets
  const walletKeysPattern = /^alaricWalletConversations_/;
  Object.keys(localStorage).forEach((key) => {
    if (walletKeysPattern.test(key)) {
      const newKey = key.replace("alaric", "quantum");
      const value = localStorage.getItem(key);
      if (value !== null) {
        localStorage.setItem(newKey, value);
        localStorage.removeItem(key);
        console.log(`Migré et supprimé ${key} vers ${newKey}`);
      }
    }
  });

  // Migrer les device IDs
  if (localStorage.getItem("alaric_device_id")) {
    localStorage.setItem(
      "quantum_device_id",
      localStorage.getItem("alaric_device_id")
    );
    localStorage.removeItem("alaric_device_id");
  }

  console.log("Migration des données terminée");
}

// Export functions
export {
  encryptData,
  decryptData,
  formatMessageContent,
  getDeviceId,
  storeDeviceId,
  storeInIndexedDB,
  showConfirm,
  showNotification,
  updateNotification,
  getNotificationColor,
  migrateStorageData,
};
